import React, { useCallback, useContext, useMemo } from 'react';
import { AuthContext } from 'contexts';
import { featureEnabled } from 'utils';
import { Button, Color, Icon, Text } from '@bb/elements-web-rebranding';
import styled from 'styled-components';
import { SupraSystemSubscriptionStatus } from '@bb/domain/dist/types';
import { useHistory } from 'react-router';

const PayNowBanner: React.FC = () => {
  const { auth } = useContext(AuthContext);
  const history = useHistory();

  const gotToBillingPage = useCallback(() => {
    history.push(`/profile?tab=BILLING&payNow=true`);
  }, [history]);

  /**
   * suspended means they are past due
   */
  const isPastDue = useMemo(
    () =>
      featureEnabled('keyholderBillingFeature') &&
      auth.supraSubscription?.status ===
        SupraSystemSubscriptionStatus.suspended,
    [auth.supraSubscription?.status],
  );

  /**
   * deactivated means they are in collections
   */
  const isInCollections = useMemo(
    () =>
      featureEnabled('keyholderBillingFeature') &&
      auth.supraSubscription?.status ===
        SupraSystemSubscriptionStatus.deactivated,
    [auth.supraSubscription?.status],
  );

  const bannerText = useMemo(
    () =>
      isInCollections
        ? 'Payment for Supra services required. Your account is past due and late fee applies.'
        : 'Payment for Supra services required. Pay now to avoid late fee.',
    [isInCollections],
  );

  if (!auth.billingCustomerNumber || (!isPastDue && !isInCollections))
    return null;

  return (
    <Container>
      <Background>
        <BannerContainer>
          <Icon icon={'closeFilled'} color="danger" size={16} />
          <BannerText>{bannerText}</BannerText>
          <Button
            size="small"
            shape="default"
            type="primary"
            onClick={gotToBillingPage}
            dataCy={'pay-now-button-banner'}
            styleOverride={customBannerButtonStyle}
          >
            Pay Now
          </Button>
        </BannerContainer>
      </Background>
    </Container>
  );
};

export default PayNowBanner;

const Container = styled.div`
  display: grid;
  background-color: white;
`;

const Background = styled.div`
  display: flex;
  margin: 16px;
  background-color: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 8px;
`;

const BannerContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  padding: 12px 14px;
  align-items: center;
`;

const BannerText = styled(Text)`
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  color: ${Color.PRIMARY_TEXT};
  align-self: center;
  margin-left: 12px;
  margin-right: 12px;
  flex: 1;
`;

/**
 * Custom style because we need the banner to be pretty small height wise.
 * This makes it so there's only horizontal padding and the button shrinks
 * to fit the content horizontally as well.
 */
const customBannerButtonStyle = {
  padding: '0px 16px',
  minWidth: 0,
};
